// Thermal printer library — direct printing via Web Bluetooth / Web USB / System dialog.
// ESC/POS command builder + connection helpers + receipt formatter.
// Tested with: 58mm BT printers (Goojprt PT-210, Xprinter XP-58), 80mm USB printers.

// ============ ESC/POS Commands ============
const ESC = 0x1B;
const GS = 0x1D;
const LF = 0x0A;

export const ESCPOS = {
  INIT: [ESC, 0x40],                 // initialize printer
  ALIGN_LEFT: [ESC, 0x61, 0x00],
  ALIGN_CENTER: [ESC, 0x61, 0x01],
  ALIGN_RIGHT: [ESC, 0x61, 0x02],
  BOLD_ON: [ESC, 0x45, 0x01],
  BOLD_OFF: [ESC, 0x45, 0x00],
  DOUBLE_ON: [ESC, 0x21, 0x10],     // double height
  DOUBLE_OFF: [ESC, 0x21, 0x00],
  CUT_FULL: [GS, 0x56, 0x00],       // full cut
  CUT_PARTIAL: [GS, 0x56, 0x01],
  FEED_LINES: (n) => [ESC, 0x64, n & 0xFF],
  DRAWER_OPEN: [ESC, 0x70, 0x00, 0x32, 0xFA],  // open cash drawer pin 2
  LF: [LF],
};

// ============ Receipt formatter ============
const charsPerLine = (paperWidth) => (paperWidth === "80mm" ? 48 : 32);

function padRight(s, len) { s = String(s ?? ""); return s.length >= len ? s.slice(0, len) : s + " ".repeat(len - s.length); }
function padLeft(s, len) { s = String(s ?? ""); return s.length >= len ? s.slice(0, len) : " ".repeat(len - s.length) + s; }
function center(s, len) {
  s = String(s ?? "");
  if (s.length >= len) return s.slice(0, len);
  const space = Math.floor((len - s.length) / 2);
  return " ".repeat(space) + s;
}
function lineDivider(len, ch = "-") { return ch.repeat(len); }
function fmtRp(n) { return "Rp" + (Math.round(parseFloat(n || 0))).toLocaleString("id-ID"); }
function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return String(iso || "-"); }
}

// Encode text to bytes (CP-437/Latin-1 fallback; most thermal printers accept ASCII fine)
function textBytes(s) {
  const enc = new TextEncoder();
  return Array.from(enc.encode(s));
}

/**
 * Build ESC/POS bytes for a transaksi receipt.
 * @param {object} trx — { no_transaksi, tanggal, vendor_nama, employee_nama, no_karyawan, no_rekening_koperasi, items: [{nama, qty, harga_satuan, subtotal}], total, catatan, periode_potong }
 * @param {object} vendor — { nama }
 * @param {object} cfg — printer_config { paper_width, header_lines[], footer_lines[], cut_paper, open_drawer }
 */
export function buildReceiptBytes(trx, vendor, cfg) {
  const width = charsPerLine(cfg.paper_width);
  const cmds = [];
  const push = (arr) => cmds.push(...arr);

  push(ESCPOS.INIT);

  // Header (centered, bold, double height vendor name)
  push(ESCPOS.ALIGN_CENTER);
  push(ESCPOS.BOLD_ON);
  push(ESCPOS.DOUBLE_ON);
  push(textBytes((vendor?.nama || trx.vendor_nama || "VENDOR") + "\n"));
  push(ESCPOS.DOUBLE_OFF);
  push(ESCPOS.BOLD_OFF);

  for (const line of (cfg.header_lines || [])) {
    if (!line) continue;
    push(textBytes(String(line) + "\n"));
  }

  push(textBytes("KOPERASI PT FIVEHOSTER\n"));
  push(textBytes(lineDivider(width) + "\n"));

  // Trx info (left-aligned)
  push(ESCPOS.ALIGN_LEFT);
  push(textBytes(`No   : ${trx.no_transaksi || "-"}\n`));
  push(textBytes(`Tgl  : ${fmtDate(trx.created_at || trx.tanggal)}\n`));
  push(textBytes(`Kasir: ${vendor?.nama || trx.vendor_nama || "-"}\n`));
  push(textBytes(`Anggt: ${trx.employee_nama || "-"}\n`));
  push(textBytes(`No.Kp: ${trx.no_karyawan || "-"} / ${trx.no_rekening_koperasi || "-"}\n`));
  push(textBytes(lineDivider(width) + "\n"));

  // Items — name on one line, qty x harga = subtotal on next
  for (const it of (trx.items || [])) {
    push(ESCPOS.BOLD_ON);
    push(textBytes(String(it.nama || "-") + "\n"));
    push(ESCPOS.BOLD_OFF);
    const left = `  ${it.qty}x ${fmtRp(it.harga_satuan)}`;
    const right = fmtRp(it.subtotal || it.qty * it.harga_satuan);
    const remain = width - left.length;
    push(textBytes(left + padLeft(right, Math.max(1, remain)) + "\n"));
  }
  push(textBytes(lineDivider(width) + "\n"));

  // Total
  push(ESCPOS.BOLD_ON);
  push(ESCPOS.DOUBLE_ON);
  const tot = fmtRp(trx.total);
  const lbl = "TOTAL";
  push(textBytes(padRight(lbl, Math.floor(width / 2)) + padLeft(tot, Math.ceil(width / 2)) + "\n"));
  push(ESCPOS.DOUBLE_OFF);
  push(ESCPOS.BOLD_OFF);

  // Status & periode
  push(textBytes(`Status: TERHUTANG (potong gaji ${trx.periode_potong || "-"})\n`));
  if (trx.catatan) push(textBytes(`Catatan: ${trx.catatan}\n`));

  push(textBytes(lineDivider(width) + "\n"));

  // Footer
  push(ESCPOS.ALIGN_CENTER);
  for (const line of (cfg.footer_lines || [])) {
    if (!line) continue;
    push(textBytes(String(line) + "\n"));
  }
  push(textBytes("\n"));

  // Feed + cut
  push(ESCPOS.FEED_LINES(3));
  if (cfg.cut_paper) push(ESCPOS.CUT_PARTIAL);
  if (cfg.open_drawer) push(ESCPOS.DRAWER_OPEN);

  return new Uint8Array(cmds);
}

// ============ Bluetooth connection ============
// Known ESC/POS Bluetooth services (added to optionalServices so we can access them)
const BT_SERVICES = [
  "000018f0-0000-1000-8000-00805f9b34fb",   // Most generic ESC/POS (e.g. Goojprt, MTP)
  "0000ff00-0000-1000-8000-00805f9b34fb",   // Xprinter, common Chinese BT printers
  "0000ffe0-0000-1000-8000-00805f9b34fb",   // HM-10 BLE module (used by some printers)
  "0000ffb0-0000-1000-8000-00805f9b34fb",
  "00001800-0000-1000-8000-00805f9b34fb",   // Generic Access (standard)
  "00001801-0000-1000-8000-00805f9b34fb",   // Generic Attribute (standard)
  "0000180f-0000-1000-8000-00805f9b34fb",   // Battery Service
  "49535343-fe7d-4ae5-8fa9-9fafd205e455",   // ISSC printers (e.g. some 80mm)
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2",   // Polar printers
];

let _btDevice = null;
let _btChar = null;

async function _ensureGattConnected(device, opts = {}) {
  const settleMs = opts.settleMs || 1500;  // longer settle is key for cheap printers
  const maxAttempts = opts.maxAttempts || 4;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      if (device.gatt.connected) {
        try { device.gatt.disconnect(); } catch (_) { /* noop */ }
        await new Promise((r) => setTimeout(r, 300));
      }
      const server = await device.gatt.connect();
      // Wait for stabilisation — many low-end printers drop GATT if queried too fast
      await new Promise((r) => setTimeout(r, settleMs));
      if (server.connected) return server;
    } catch (e) {
      if (attempt === maxAttempts - 1) throw e;
    }
    await new Promise((r) => setTimeout(r, 400));
  }
  throw new Error("Tidak bisa menjaga koneksi GATT setelah beberapa percobaan.");
}

async function _scanForWritableChar(server, device) {
  // Try getPrimaryServices with retry-on-disconnect
  let lastErr = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      if (!server.connected) {
        server = await _ensureGattConnected(device);
      }
      const services = await server.getPrimaryServices();
      if (!services || services.length === 0) {
        throw new Error("Device tidak expose service apapun. Mungkin bukan printer ESC/POS BLE.");
      }
      const prioritised = [...services].sort((a, b) => {
        const ai = BT_SERVICES.indexOf((a.uuid || "").toLowerCase());
        const bi = BT_SERVICES.indexOf((b.uuid || "").toLowerCase());
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      });
      for (const svc of prioritised) {
        try {
          const chars = await svc.getCharacteristics();
          for (const c of chars) {
            if (c.properties.write || c.properties.writeWithoutResponse) {
              return { server, char: c };
            }
          }
        } catch (_) { /* skip */ }
      }
      throw new Error("Tidak ada karakteristik write/writeWithoutResponse pada service yang tersedia.");
    } catch (e) {
      lastErr = e;
      // Retry only if message indicates disconnect
      if (String(e?.message || e).toLowerCase().includes("disconnect")) {
        await new Promise((r) => setTimeout(r, 600));
        try { server = await _ensureGattConnected(device); } catch (_) { /* noop */ }
        continue;
      }
      throw e;
    }
  }
  throw lastErr || new Error("Gagal scan service printer.");
}

export async function btConnect() {
  if (!navigator.bluetooth) throw new Error("Browser tidak mendukung Web Bluetooth. Gunakan Chrome / Edge desktop atau Chrome Android.");
  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: BT_SERVICES,
  });

  device.addEventListener("gattserverdisconnected", () => { _btChar = null; });

  let server = await _ensureGattConnected(device);
  let result;
  try {
    result = await _scanForWritableChar(server, device);
  } catch (e) {
    try { device.gatt.disconnect(); } catch (_) { /* noop */ }
    const name = device.name || "(tanpa nama)";
    throw new Error(
      `Gagal menemukan service ESC/POS pada device "${name}". Kemungkinan penyebab:\n` +
      `• Printer Anda menggunakan Bluetooth Classic / SPP (bukan BLE). Web Bluetooth hanya support BLE.\n` +
      `• Belum di-pair via OS. Buka Settings → Bluetooth, pair printer dulu, lalu coba lagi.\n` +
      `• Printer butuh PIN/passkey saat pairing.\n` +
      `Detail teknis: ${e.message || e}`
    );
  }
  _btDevice = device; _btChar = result.char;
  return { name: device.name || "BT Printer", id: device.id };
}

export async function btPrint(bytes) {
  if (!_btChar || !_btDevice) throw new Error("Printer Bluetooth belum terhubung. Klik 'Pasangkan Printer'.");

  // Auto-reconnect if GATT dropped between print sessions
  if (!_btDevice.gatt?.connected) {
    try {
      const server = await _ensureGattConnected(_btDevice);
      // Re-acquire characteristic from same UUID
      const services = await server.getPrimaryServices();
      let newChar = null;
      for (const svc of services) {
        try {
          if (svc.uuid === _btChar.service?.uuid) {
            const chars = await svc.getCharacteristics();
            newChar = chars.find((c) => c.uuid === _btChar.uuid) || chars.find((c) => c.properties.write || c.properties.writeWithoutResponse);
            if (newChar) break;
          }
        } catch (_) { /* skip */ }
      }
      if (!newChar) throw new Error("Tidak bisa re-acquire karakteristik setelah reconnect.");
      _btChar = newChar;
    } catch (e) {
      throw new Error("Printer disconnect & gagal reconnect: " + (e.message || e));
    }
  }

  // Chunk write (BLE has limited MTU ~ 20-180 bytes per write).
  // 150 is a safe default; printers like Goojprt 58mm accept up to ~180.
  const CHUNK = 150;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.slice(i, i + CHUNK);
    try {
      if (_btChar.properties.writeWithoutResponse) {
        await _btChar.writeValueWithoutResponse(slice);
      } else {
        await _btChar.writeValue(slice);
      }
    } catch (e) {
      // Some printers reject writeWithoutResponse mid-stream; try fallback
      try { await _btChar.writeValue(slice); } catch (_) { throw e; }
    }
    // Small pacing delay so printer buffer doesn't overflow
    await new Promise((r) => setTimeout(r, 25));
  }
}

export function btIsConnected() {
  return !!(_btDevice && _btDevice.gatt?.connected && _btChar);
}

export async function btDisconnect() {
  try { if (_btDevice?.gatt?.connected) _btDevice.gatt.disconnect(); } catch (_) { /* noop */ }
  _btDevice = null; _btChar = null;
}

// ============ USB connection ============
let _usbDevice = null;
let _usbInterface = 0;
let _usbEndpoint = 1;

export async function usbConnect() {
  if (!navigator.usb) throw new Error("Browser tidak mendukung WebUSB. Gunakan Chrome / Edge desktop atau Chrome Android.");
  const device = await navigator.usb.requestDevice({ filters: [] });
  await device.open();
  if (device.configuration === null) await device.selectConfiguration(1);
  const iface = device.configuration.interfaces[0];
  await device.claimInterface(iface.interfaceNumber);
  // Find bulk OUT endpoint
  const alt = iface.alternates[0];
  let outEp = null;
  for (const ep of alt.endpoints) {
    if (ep.direction === "out" && ep.type === "bulk") { outEp = ep.endpointNumber; break; }
  }
  if (!outEp) throw new Error("Tidak menemukan endpoint USB untuk printer ini.");
  _usbDevice = device; _usbInterface = iface.interfaceNumber; _usbEndpoint = outEp;
  return { name: device.productName || "USB Printer", id: device.serialNumber || "" };
}

export async function usbPrint(bytes) {
  if (!_usbDevice) throw new Error("Printer USB belum terhubung. Klik 'Pasangkan Printer'.");
  await _usbDevice.transferOut(_usbEndpoint, bytes);
}

export function usbIsConnected() {
  return !!_usbDevice;
}

// ============ Print orchestration ============
export async function printReceipt(trx, vendor, cfg) {
  const bytes = buildReceiptBytes(trx, vendor, cfg);
  if (cfg.connection_type === "bluetooth") {
    if (!btIsConnected()) throw new Error("Printer Bluetooth belum terhubung. Pasangkan dulu di Pengaturan Printer.");
    return btPrint(bytes);
  }
  if (cfg.connection_type === "usb") {
    if (!usbIsConnected()) throw new Error("Printer USB belum terhubung. Pasangkan dulu di Pengaturan Printer.");
    return usbPrint(bytes);
  }
  // Fallback: browser native print (with dialog) — caller should use legacy HTML method instead
  throw new Error("Connection type 'system' menggunakan dialog browser. Gunakan tombol Print legacy.");
}
